import type {
  CustomChallenge,
  CustomPost2,
  CustomPostReview,
  WithRoute,
} from '~/types'

import type { Get } from 'type-fest'

type OfferPath = 'community.content_blocks.0.content_block_type'

type Union = CustomChallenge | CustomPost2 | CustomPostReview

type GetContentBlockType<T extends Union> = Get<T, OfferPath>[]

type GetOffer<T extends Union> = WithRoute<Get<T, `${OfferPath}.offers.0`>>

type Return<T extends Union> = {
  contentBlocksType: ComputedRef<GetContentBlockType<T> | undefined>
  offer: ComputedRef<GetOffer<T> | undefined>
}

type PostParams = Ref<WithRoute<CustomPost2> | undefined>
type ChallengeParams = Ref<CustomChallenge | undefined>
type WaperOpinionParams = Ref<WithRoute<CustomPostReview> | undefined>
type ParamsUnion = PostParams | ChallengeParams | WaperOpinionParams

export function useOffer<T extends ParamsUnion>(
  item: T,
): T extends ChallengeParams
  ? Return<CustomChallenge>
  : T extends PostParams
    ? Return<CustomPost2>
    : Return<CustomPostReview>

export function useOffer(item: ParamsUnion) {
  const contentBlocksType = computed(() => {
    const contentBlocks = item.value?.community?.content_blocks

    return (
      contentBlocks?.flatMap((item) => {
        return item['content_block_type']
      }) ?? []
    )
  })

  const offer = computed(() => {
    const offersBlock = contentBlocksType.value?.find(
      (block) => block?.slug === 'offres-du-moment',
    )

    return offersBlock?.offers?.length
      ? {
          ...offersBlock?.offers[0],
          route: `/offres/${offersBlock?.offers[0].slug}`,
        }
      : undefined
  })

  return {
    offer,
    contentBlocksType,
  }
}
